import React from 'react';
import { Box, Typography, Paper, Button, TextField, Select, MenuItem, FormControl, InputLabel, Stepper, Step, StepLabel } from '@mui/material';
import { AnimatedChatDemo } from '../components/DemoComponents/AnimatedChatDemo';
import { AnimatedTicketListDemo } from '../components/DemoComponents/AnimatedTicketListDemo';
import { AnimatedTicketDetailDemo } from '../components/DemoComponents/AnimatedTicketDetailDemo';
import { AnimatedAdminConsoleDemo } from '../components/DemoComponents/AnimatedAdminConsoleDemo';
import { DemoTicketBoard } from '../components/DemoComponents/DemoTicketBoard';
import { DemoApprovalFlow } from '../components/DemoComponents/DemoApprovalFlow';

// Demo Components
// Commented out unused component to fix ESLint warnings
// const DemoTicketCard: React.FC<{ ticket: typeof mockTickets[0] }> = ({ ticket }) => {
//   const { mode } = useKBTheme();
//   return (
//     <Paper
//       sx={{
//         p: 2,
//         mb: 2,
//         background: mode === 'dark' ? 'rgba(71, 85, 105, 0.4)' : 'rgba(241, 245, 249, 0.8)',
//         borderRadius: 2,
//       }}
//     >
//       <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: mode === 'dark' ? '#fff' : '#0f172a' }}>
//         {ticket.summary}
//       </Typography>
//       <Typography variant="body2" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#64748b', mb: 1 }}>
//         {ticket.description}
//       </Typography>
//       <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
//         <Chip label={ticket.status} size="small" />
//         <Chip label={ticket.priority} size="small" />
//       </Box>
//     </Paper>
//   );
// };

const DemoSmartPolicyForm: React.FC = () => {
  const [formData, setFormData] = React.useState({
    name: 'Software Installation',
    keywords: 'install, software, adobe, photoshop, creative suite',
    category: 'Software Installation',
    priority: 'medium',
    team: 'IT',
    approver: '',
    estimatedResolution: '1-2 business days',
  });
  const [step, setStep] = React.useState(0);
  const [saved, setSaved] = React.useState(false);

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => {
      setSaved(false);
      setStep(0);
      setFormData({
        name: 'Software Installation',
        keywords: 'install, software, adobe, photoshop, creative suite',
        category: 'Software Installation',
        priority: 'medium',
        team: 'IT',
        approver: '',
        estimatedResolution: '1-2 business days',
      });
    }, 2000);
  };

  React.useEffect(() => {
    // Auto-animate through steps
    const stepTimer = setInterval(() => {
      setStep((prev) => {
        if (prev === 2) {
          // Reset after showing all steps
          setTimeout(() => {
            setStep(0);
            setSaved(false);
          }, 2000);
          return prev;
        }
        return prev + 1;
      });
    }, 4000);

    return () => clearInterval(stepTimer);
  }, []);

  return (
    <Box>
      <Stepper activeStep={step} sx={{ mb: 3 }}>
        <Step><StepLabel>Basic Info</StepLabel></Step>
        <Step><StepLabel>Configuration</StepLabel></Step>
        <Step><StepLabel>Approval</StepLabel></Step>
      </Stepper>

      {step === 0 && (
        <Box>
          <TextField
            fullWidth
            label="Policy Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            sx={{ mb: 2 }}
            size="small"
          />
          <TextField
            fullWidth
            label="Keywords (comma-separated)"
            value={formData.keywords}
            onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
            sx={{ mb: 2 }}
            size="small"
            multiline
            rows={2}
            helperText="Keywords that trigger this policy"
          />
          <Button
            variant="contained"
            fullWidth
            onClick={() => setStep(1)}
            sx={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', mt: 2 }}
          >
            Next: Configuration
          </Button>
        </Box>
      )}

      {step === 1 && (
        <Box>
          <FormControl fullWidth sx={{ mb: 2 }} size="small">
            <InputLabel>Category</InputLabel>
            <Select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} label="Category">
              <MenuItem value="Software Installation">Software Installation</MenuItem>
              <MenuItem value="Hardware Issues">Hardware Issues</MenuItem>
              <MenuItem value="Account Management">Account Management</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }} size="small">
            <InputLabel>Priority</InputLabel>
            <Select value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: e.target.value })} label="Priority">
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }} size="small">
            <InputLabel>Assigned Team</InputLabel>
            <Select value={formData.team} onChange={(e) => setFormData({ ...formData, team: e.target.value })} label="Assigned Team">
              <MenuItem value="IT">IT</MenuItem>
              <MenuItem value="Support">Support</MenuItem>
              <MenuItem value="HR">HR</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Estimated Resolution"
            value={formData.estimatedResolution}
            onChange={(e) => setFormData({ ...formData, estimatedResolution: e.target.value })}
            sx={{ mb: 2 }}
            size="small"
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="outlined" fullWidth onClick={() => setStep(0)}>
              Back
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={() => setStep(2)}
              sx={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}
            >
              Next: Approval
            </Button>
          </Box>
        </Box>
      )}

      {step === 2 && (
        <Box>
          <FormControl fullWidth sx={{ mb: 2 }} size="small">
            <InputLabel>Require Approval</InputLabel>
            <Select
              value={formData.approver}
              onChange={(e) => setFormData({ ...formData, approver: e.target.value })}
              label="Require Approval"
            >
              <MenuItem value="">No approval required</MenuItem>
              <MenuItem value="john.doe@company.com">John Doe (IT Manager)</MenuItem>
              <MenuItem value="jane.smith@company.com">Jane Smith (HR Manager)</MenuItem>
            </Select>
          </FormControl>
          {formData.approver && (
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'rgba(99, 102, 241, 0.1)', border: '1px solid #6366f1' }}>
              <Typography variant="caption" sx={{ color: '#6366f1', fontWeight: 600 }}>
                When tickets match this policy, they will require approval from {formData.approver.split('@')[0]}
              </Typography>
            </Paper>
          )}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="outlined" fullWidth onClick={() => setStep(1)}>
              Back
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={handleSave}
              disabled={saved}
              sx={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}
            >
              {saved ? '✓ Policy Saved!' : 'Save Policy'}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// Article Content Components
const createArticleContent = (content: string | React.ReactNode): React.ReactNode => {
  if (typeof content === 'string') {
    return (
      <>
        {content.split('\n\n').map((paragraph, idx) => (
          <Typography key={idx} variant="body1" component="p" sx={{ mb: 2 }}>
            {paragraph}
          </Typography>
        ))}
      </>
    );
  }
  return content;
};

export interface Article {
  id: string;
  title: string;
  description: string;
  category: string;
  tags: string[];
  readTime: number;
  content: React.ReactNode;
  demo?: React.ReactNode;
  lastUpdated?: string;
}

export const articles: Article[] = [
  {
    id: 'welcome-to-coresupport',
    title: 'Welcome to CoreSupport',
    description: 'Get started with your expert AI agent and learn the basics of CoreSupport.',
    category: 'Getting Started',
    tags: ['basics', 'introduction', 'getting-started'],
    readTime: 3,
    content: createArticleContent(
      `Welcome to CoreSupport! We deploy expert AI agents that learn from your knowledge base and workflows. Your agent becomes an expert on your organization—capturing institutional knowledge, avoiding knowledge loss, and eliminating time wasted on knowledge transfers.

CoreSupport handles simple requests instantly, creates tickets for complex issues, and assists your human agents. Your agent learns from your documentation, policies, and real workflows to become an expert on your organization.

What makes CoreSupport different:
- Dedicated AI expert for your organization
- Trained on your knowledge and workflows
- Deploys in minutes, not weeks
- Eliminates knowledge loss when team members leave
- Automates routine tasks instantly
- Creates and routes tickets automatically

Your agent preserves and applies your institutional knowledge, making it instantly accessible to your entire team.`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'getting-started-admin',
    title: 'Getting Started with Admin Console',
    description: 'Learn how to navigate and use the Admin Console to manage your organization.',
    category: 'Admin Console',
    tags: ['admin', 'console', 'management'],
    readTime: 5,
    demo: <AnimatedAdminConsoleDemo />,
    content: createArticleContent(
      `The Admin Console is your central hub for managing your CoreSupport organization. From here, you can manage users, teams, roles, documents, and Smart Policies.

Key Features:
- Dashboard: Overview of your organization's activity and metrics
- Users: Invite, edit, and manage user accounts
- Teams: Create and organize teams for ticket assignment
- Roles: Set up role-based access control
- Documents: Upload and manage your knowledge base
- Smart Policies: Configure automatic ticket creation and routing

The Admin Console is accessible only to users with admin privileges. If you need admin access, contact your organization's administrator.

To get started:
1. Log in to the Admin Console
2. Explore the Dashboard to see your organization's overview
3. Set up your first team in the Teams section
4. Upload documents to build your knowledge base
5. Configure Smart Policies to automate ticket creation`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'what-are-smart-ticket-rules',
    title: 'What Are Smart Policies?',
    description: 'Understand how Smart Policies automatically create and route tickets based on keywords.',
    category: 'Smart Policies',
    tags: ['smart-policies', 'automation', 'tickets'],
    readTime: 7,
    content: createArticleContent(
      `Smart Policies are powerful automation tools that automatically create tickets when specific keywords are detected in user conversations with your AI agent. This eliminates manual ticket creation and ensures requests are routed to the right team instantly.

How Smart Policies Work:
When a user chats with your AI agent, the agent analyzes the conversation for keywords defined in your Smart Policies. If a match is found, the agent automatically:
1. Creates a ticket with the appropriate details
2. Assigns it to the correct team
3. Sets the priority and category
4. Provides an estimated resolution time

For example, if a user says "I need Adobe Photoshop installed," and you have a Smart Policy with keywords like "install" and "software," the agent will automatically create a ticket, assign it to your IT team, and inform the user.

Benefits:
- Automatic ticket creation saves time
- Consistent routing ensures tickets reach the right team
- Users get immediate confirmation and estimated resolution times
- Reduces manual work for your support team
- Ensures nothing falls through the cracks

Smart Policies work seamlessly with your chat interface. When users interact with your AI agent, the agent can handle simple questions directly or create tickets for complex issues automatically.`
    ),
    demo: <DemoSmartPolicyForm />,
    lastUpdated: '2025-01-15',
  },
  {
    id: 'creating-smart-ticket-rule',
    title: 'Creating Your First Smart Policy',
    description: 'Step-by-step guide to creating a Smart Policy that automatically creates tickets.',
    category: 'Smart Policies',
    tags: ['smart-policies', 'setup', 'configuration'],
    readTime: 8,
    content: createArticleContent(
      `Creating your first Smart Policy is straightforward. Follow these steps to set up automated ticket creation for common requests.

Step 1: Navigate to Smart Policies
1. Log in to the Admin Console
2. Click on "Smart Policies" in the sidebar
3. Click the "Create New Rule" button

Step 2: Configure the Rule
- Name: Give your rule a descriptive name (e.g., "Software Installation")
- Description: Explain what this rule does
- Keywords: Add keywords that trigger this rule (comma-separated)
  - Example: "install, software, adobe, creative suite, photoshop"
  - Think about different ways users might phrase the request
- Category: Select the ticket category (e.g., "Software Installation")
- Priority: Set the default priority (low, medium, high, urgent)
- Assigned Team: Choose which team should receive these tickets
- Estimated Resolution: Set expectations (e.g., "1-2 business days")

Step 3: Set Up Approvers (Optional)
If certain tickets require approval before processing:
- Enable "Require Approval"
- Select an approver from your organization
- The approver will receive email notifications when approval is needed

Step 4: Activate the Rule
- Toggle "Active" to enable the rule
- Click "Save Rule"

Your rule is now active! When users mention any of your keywords in chat, tickets will be created automatically. Test your rule by having a user chat with your AI agent and mention one of your keywords.`
    ),
    demo: <DemoSmartPolicyForm />,
    lastUpdated: '2025-01-15',
  },
  {
    id: 'setting-up-approvers',
    title: 'Setting Up Approvers in Smart Policies',
    description: 'Learn how to configure approval workflows for tickets created by Smart Policies.',
    category: 'Smart Policies',
    tags: ['approvals', 'smart-policies', 'workflow'],
    readTime: 6,
    content: createArticleContent(
      `Some tickets require approval before they can be processed. Smart Policies allow you to configure approvers so that tickets are automatically routed for approval when needed.

Why Use Approvers:
- Ensure compliance with company policies
- Control access to sensitive resources
- Maintain budget oversight
- Require manager approval for certain requests

Setting Up an Approver:
1. When creating or editing a Smart Policy, find the "Approver" section
2. Select a user from your organization who should approve these tickets
3. When a ticket is created that matches this rule, an approval request is automatically sent
4. The approver receives an email notification with ticket details
5. The approver can approve or reject the request directly from the ticket

Approval Workflow:
1. User chats with AI agent and mentions keywords
2. Ticket is automatically created
3. Approval request is sent to the designated approver
4. Approver reviews and makes a decision
5. Requester receives notification of the decision
6. If approved, ticket proceeds to assigned team

You can set up multiple approvers for different types of requests. For example, software installations might require IT manager approval, while badge requests might require HR approval.`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'how-approval-workflows-work',
    title: 'How Approval Workflows Work',
    description: 'Understand the complete approval workflow from request to decision.',
    category: 'Smart Policies',
    tags: ['approvals', 'workflow', 'process'],
    readTime: 8,
    content: createArticleContent(
      `Approval workflows ensure that important requests are reviewed before being processed. Here's how the complete workflow operates:

The Approval Process:

1. Ticket Creation
   - User interacts with AI agent or creates ticket manually
   - Smart Policy detects keywords and creates ticket
   - If rule specifies an approver, approval request is created

2. Approval Request
   - Approver receives email notification
   - Email includes ticket details, requester information, and a direct link
   - Approver can click "Review & Approve" to go to the ticket

3. Approval Decision
   - Approver views ticket details in the Tickets App
   - Approval section appears at the top of the ticket
   - Approver can:
     - Approve: Ticket proceeds to assigned team
     - Reject: Provide reason for rejection (optional)
     - Request More Information: Add comments

4. Notification
   - Requester receives email with decision
   - If approved, ticket status updates to "in_progress"
   - If rejected, ticket status remains, and requester can see the reason

5. Processing
   - Approved tickets are assigned to the designated team
   - Team processes the request according to the ticket details
   - Requester is notified when the ticket is resolved

Best Practices:
- Set clear expectations in Smart Policies about what requires approval
- Choose approvers who can respond promptly
- Provide context in ticket descriptions to help approvers make decisions
- Use rejection reasons to help requesters understand requirements`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'starting-first-conversation',
    title: 'Starting Your First Conversation',
    description: 'Learn how to chat with your AI agent and get instant help.',
    category: 'Chat Interface',
    tags: ['chat', 'ai-agent', 'conversation'],
    readTime: 4,
    demo: <AnimatedChatDemo />,
    content: createArticleContent(
      `Your AI agent is ready to help! Starting a conversation is simple and intuitive.

Getting Started:
1. Navigate to the Chat interface
2. You'll see a chat window with your AI agent
3. Type your question or request in the message box
4. Press Enter or click Send

Your AI agent can:
- Answer questions instantly using your knowledge base
- Help with common tasks and procedures
- Create tickets automatically for complex issues
- Provide step-by-step guidance
- Suggest relevant actions

Example Conversations:
- "How do I reset my password?" → Direct answer with instructions
- "I need Adobe Photoshop installed" → Automatic ticket creation
- "What's our vacation policy?" → Answer from knowledge base
- "My laptop screen is flickering" → Ticket creation with IT team assignment

The agent understands context and can handle follow-up questions. You can have natural conversations, and the agent will remember what you've discussed in the current session.

Tips for Best Results:
- Be specific about what you need
- Provide relevant details (e.g., software name, device type)
- Ask follow-up questions if something isn't clear
- Use the suggested actions that appear after responses`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'creating-tickets-from-chat',
    title: 'Creating Tickets from Chat (How Smart Rules Work)',
    description: 'Understand how tickets are automatically created when you chat with your AI agent.',
    category: 'Chat Interface',
    tags: ['chat', 'tickets', 'smart-policies'],
    readTime: 6,
    demo: <AnimatedChatDemo />,
    content: createArticleContent(
      `When you chat with your AI agent, tickets are automatically created for complex issues based on Smart Policies. Here's how it works:

Automatic Ticket Creation:
1. You send a message to your AI agent
2. The agent analyzes your message for keywords
3. If keywords match a Smart Policy:
   - A ticket is automatically created
   - The ticket is assigned to the appropriate team
   - Priority and category are set based on the rule
   - You receive confirmation with an estimated resolution time

Example Flow:
You: "I need Adobe Creative Suite installed on my work laptop"
Agent: "I'll create a support ticket for your Adobe Creative Suite installation request. Our IT team will contact you within 1-2 business days."
[Ticket T-1234 is automatically created and assigned to IT team]

When Tickets Are Created:
- Complex issues that require human attention
- Requests that match Smart Policy keywords
- Issues that need approval
- Problems that can't be resolved instantly

When Tickets Are NOT Created:
- Simple questions that can be answered directly
- General information requests
- Procedures that can be explained step-by-step

You can view all your tickets by clicking "My Tickets" in the chat sidebar. Each ticket shows:
- Ticket ID and summary
- Status and priority
- Assigned team
- Estimated resolution time
- Full conversation history

The agent is smart enough to know when to create a ticket and when to provide direct help. This ensures you get instant answers for simple questions and proper tracking for complex issues.`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'creating-ticket-manual',
    title: 'Creating a New Ticket (Manual vs Automatic)',
    description: 'Learn when to create tickets manually and when they\'re created automatically.',
    category: 'Tickets App',
    tags: ['tickets', 'creation', 'manual'],
    readTime: 5,
    content: createArticleContent(
      `Tickets can be created in two ways: automatically by your AI agent (via Smart Policies) or manually by you or your team.

Automatic Ticket Creation:
- Triggered when you chat with AI agent and mention keywords
- Handled by Smart Policies
- No action needed from you
- Instant confirmation and routing

Manual Ticket Creation:
Sometimes you need to create a ticket directly, especially when:
- You know exactly what needs to be done
- The issue doesn't match any Smart Policies
- You want to provide detailed information upfront
- You're creating a ticket on behalf of someone else

How to Create a Ticket Manually:
1. Navigate to the Tickets App
2. Click the "Create Ticket" button (usually a "+" icon)
3. Fill in the ticket details:
   - Summary: Brief description of the issue
   - Description: Detailed information
   - Priority: Low, Medium, High, or Urgent
   - Category: Select the appropriate category
   - Assign To: Choose a team member (optional)
   - Team: Select the team that should handle this
4. Click "Create Ticket"

The ticket is now created and visible to the assigned team. You can track its progress, add comments, and see updates in real-time.

Best Practices:
- Use clear, descriptive summaries
- Provide all relevant details in the description
- Set appropriate priority (don't mark everything as urgent)
- Assign to the correct team for faster routing
- Attach screenshots or files if helpful`
    ),
    demo: <AnimatedTicketListDemo />,
    lastUpdated: '2025-01-15',
  },
  {
    id: 'understanding-teams',
    title: 'Understanding Teams in CoreSupport',
    description: 'Learn how teams work and how they are used for ticket assignment and organization.',
    category: 'Teams & Roles',
    tags: ['teams', 'organization', 'structure'],
    readTime: 6,
    content: createArticleContent(
      `Teams are the foundation of organization in CoreSupport. They help route tickets to the right people and structure your support organization.

What Are Teams?
Teams are groups of users who work together to handle specific types of requests. Common teams include:
- IT: Handles technical issues, software installations, hardware problems
- HR: Manages employee requests, badges, benefits
- Support: Provides general customer support
- Finance: Handles billing and financial requests

How Teams Work:
1. Ticket Assignment: When tickets are created (automatically or manually), they're assigned to teams
2. Team Members: Team members can see and work on tickets assigned to their team
3. Smart Policies: Rules can automatically assign tickets to specific teams
4. Round-Robin: If multiple team members are available, tickets are distributed evenly

Creating Teams:
1. Go to Admin Console → Teams
2. Click "Create Team"
3. Enter team name and description
4. Add team members
5. Assign a team manager (optional)
6. Set parent team if creating a hierarchy (optional)

Team Hierarchy:
You can create nested teams for better organization:
- IT Team
  - IT Support (sub-team)
  - IT Infrastructure (sub-team)
- HR Team
  - HR Operations (sub-team)
  - HR Benefits (sub-team)

Benefits:
- Clear ownership of tickets
- Better workload distribution
- Easier reporting and analytics
- Organized structure matching your organization
- Automatic routing via Smart Policies`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'how-roles-control-access',
    title: 'How Roles Control Access',
    description: 'Understand role-based access control and how permissions work in CoreSupport.',
    category: 'Teams & Roles',
    tags: ['roles', 'permissions', 'access-control'],
    readTime: 7,
    content: createArticleContent(
      `Roles define what users can do in CoreSupport. They control access to features, data, and actions throughout the platform.

What Are Roles?
Roles are collections of permissions that determine what actions a user can perform. Common roles include:
- Admin: Full access to all features
- Manager: Can manage teams and view analytics
- User: Basic access to chat and tickets
- Support Agent: Can handle tickets and view knowledge base

How Roles Work:
1. Permissions: Each role has specific permissions (e.g., "create_tickets", "manage_users")
2. User Assignment: Users are assigned to roles
3. Access Control: Features check role permissions before allowing actions
4. Inheritance: Some roles inherit permissions from others

Default Roles:
- Admin: Can manage everything
  - Manage users, teams, roles
  - Configure Smart Policies
  - Access all tickets and analytics
  - Upload and manage documents

- Manager: Can manage teams and view analytics
  - View team tickets
  - Assign tickets to team members
  - View analytics and reports
  - Manage team members

- User: Basic access
  - Chat with AI agent
  - Create tickets
  - View own tickets
  - Access knowledge base

Creating Custom Roles:
1. Go to Admin Console → Roles
2. Click "Create Role"
3. Name the role and add description
4. Select permissions from available list
5. Save the role
6. Assign users to the new role

Best Practices:
- Use principle of least privilege (grant minimum necessary permissions)
- Create roles that match your organization's structure
- Document what each role can do
- Review roles periodically to ensure they're still appropriate
- Use role names that are clear and descriptive`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'approving-rejecting-requests',
    title: 'Approving or Rejecting Requests',
    description: 'Learn how to approve or reject approval requests for tickets.',
    category: 'Tickets App',
    tags: ['approvals', 'tickets', 'workflow'],
    readTime: 5,
    content: createArticleContent(
      `When you're designated as an approver for a ticket, you'll receive an email notification and see the approval request in the Tickets App.

Receiving Approval Requests:
1. Email Notification: You'll receive an email when approval is needed
2. Ticket Visibility: The ticket appears in your Tickets App
3. Approval Section: A special section appears at the top of the ticket

Reviewing the Request:
1. Open the ticket to see full details
2. Review what's being requested
3. Check the requester's information
4. Consider any notes or context provided

Approving a Request:
1. Click the "Approve" button in the approval section
2. Optionally add a note explaining your decision
3. The ticket status updates to "in_progress"
4. The requester receives a confirmation email
5. The assigned team can now proceed with the request

Rejecting a Request:
1. Click the "Reject" button
2. Provide a reason for rejection (required)
3. The requester receives notification with your reason
4. The ticket status remains, and the requester can see why it was rejected

Best Practices:
- Respond promptly to approval requests
- Provide clear reasons when rejecting
- Use notes to add context or instructions
- Review ticket details carefully before deciding`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'smart-policies-best-practices',
    title: 'Smart Policies Best Practices',
    description: 'Tips and best practices for creating effective Smart Policies.',
    category: 'Best Practices',
    tags: ['smart-policies', 'best-practices', 'tips'],
    readTime: 8,
    content: createArticleContent(
      `Creating effective Smart Policies requires careful planning. Follow these best practices to ensure your rules work optimally.

Keyword Selection:
- Use specific keywords that clearly indicate the request type
- Include variations and synonyms (e.g., "install", "installation", "setup")
- Consider common misspellings or abbreviations
- Avoid overly generic keywords that might trigger false positives
- Test keywords with real user language

Rule Organization:
- Create one rule per request type
- Use descriptive names that explain the rule's purpose
- Group related rules by category
- Keep rules active only when needed
- Review and update rules periodically

Team Assignment:
- Assign tickets to the most appropriate team
- Consider team workload when assigning
- Use team hierarchy for complex organizations
- Ensure teams have the right skills for the request type

Priority Settings:
- Set realistic default priorities
- Consider urgency and business impact
- Use "high" sparingly for truly urgent issues
- Align priorities with your organization's standards

Approval Workflows:
- Only require approval when necessary
- Choose approvers who can respond quickly
- Provide clear context in ticket descriptions
- Set expectations about approval timelines

Testing:
- Test rules with sample conversations
- Verify tickets are created correctly
- Check that routing works as expected
- Ensure approvers receive notifications
- Monitor rule performance and adjust as needed

Maintenance:
- Review rules quarterly
- Update keywords based on user feedback
- Deactivate rules that are no longer needed
- Document rules for team reference
- Keep rules aligned with organizational changes`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'managing-users',
    title: 'Managing Users',
    description: 'Learn how to invite, edit, and manage users in your organization.',
    category: 'Admin Console',
    tags: ['users', 'admin', 'management'],
    readTime: 6,
    demo: <AnimatedAdminConsoleDemo />,
    content: createArticleContent(
      `User management is essential for maintaining your CoreSupport organization. You can invite new users, assign roles, and manage user access.

Inviting Users:
1. Go to Admin Console → Users
2. Click "Invite User"
3. Enter the user's email address
4. Select a role (Admin, Manager, or User)
5. Optionally assign to a team
6. Click "Send Invitation"

The user receives an email invitation and can create their account. Once they accept, they'll have access based on their assigned role.

Editing Users:
1. Find the user in the Users list
2. Click the edit icon
3. Update their information:
   - Change role
   - Add or remove from teams
   - Update email (if needed)
4. Save changes

Deleting Users:
1. Find the user in the Users list
2. Click the delete icon
3. Confirm the deletion
4. The user's access is revoked immediately

Best Practices:
- Use roles to control access appropriately
- Assign users to teams for better organization
- Review user list periodically
- Remove users who no longer need access
- Keep user information up to date`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'creating-managing-teams',
    title: 'Creating and Managing Teams',
    description: 'Step-by-step guide to creating teams and organizing your support structure.',
    category: 'Admin Console',
    tags: ['teams', 'admin', 'organization'],
    readTime: 7,
    demo: <AnimatedAdminConsoleDemo />,
    content: createArticleContent(
      `Teams help organize your support structure and route tickets effectively. Here's how to create and manage them.

Creating a Team:
1. Go to Admin Console → Teams
2. Click "Create Team"
3. Enter team details:
   - Name: Descriptive team name (e.g., "IT Support")
   - Description: What this team handles
   - Manager: Select a team manager (optional)
   - Parent Team: For hierarchical structure (optional)
4. Add team members
5. Save the team

Team Hierarchy:
You can create nested teams for better organization:
- Main Team: IT
  - Sub-team: IT Support
  - Sub-team: IT Infrastructure

Benefits:
- Clear ownership of tickets
- Better workload distribution
- Organized structure
- Easier reporting

Managing Teams:
- Edit: Update team name, description, or members
- Add Members: Add users to existing teams
- Remove Members: Remove users from teams
- Set Manager: Assign a team manager
- Delete: Remove teams that are no longer needed

Best Practices:
- Use clear, descriptive team names
- Create teams that match your organization's structure
- Assign managers for accountability
- Use hierarchy for complex organizations
- Review teams periodically`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'uploading-documents',
    title: 'Uploading Documents to Knowledge Base',
    description: 'Learn how to add documents to your knowledge base so your AI agent can learn from them.',
    category: 'Admin Console',
    tags: ['documents', 'knowledge-base', 'upload'],
    readTime: 5,
    demo: <AnimatedAdminConsoleDemo />,
    content: createArticleContent(
      `Your AI agent learns from your knowledge base documents. Upload documents to teach your agent about your organization's policies, procedures, and information.

Uploading Documents:
1. Go to Admin Console → Documents
2. Click "Upload Document"
3. Select files from your computer
4. Wait for upload and indexing to complete
5. Documents are automatically processed and added to your knowledge base

Supported Formats:
- PDF files
- Word documents (.docx)
- Text files (.txt)
- Markdown files (.md)

Best Practices:
- Upload comprehensive documentation
- Keep documents up to date
- Organize documents logically
- Include policies, procedures, and FAQs
- Remove outdated documents

Your AI agent will use these documents to answer questions and provide accurate information to users. The more comprehensive your knowledge base, the better your agent performs.`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'understanding-ai-responses',
    title: 'Understanding AI Responses',
    description: 'Learn how your AI agent provides responses and when it creates tickets.',
    category: 'Chat Interface',
    tags: ['ai', 'chat', 'responses'],
    readTime: 5,
    demo: <AnimatedChatDemo />,
    content: createArticleContent(
      `Your AI agent is trained on your knowledge base and can provide intelligent responses. Understanding how it works helps you get the most out of it.

Direct Responses:
For simple questions, the agent provides direct answers:
- Questions about policies
- How-to procedures
- General information
- Quick troubleshooting steps

The agent uses your knowledge base to find accurate information and presents it in a clear, helpful way.

Ticket Creation:
For complex issues, the agent automatically creates tickets:
- Requests that match Smart Policies
- Issues requiring human attention
- Problems that need approval
- Tasks that can't be resolved instantly

The agent tells you when a ticket is created and provides the ticket ID.

Confidence Indicators:
Some responses include confidence scores indicating how certain the agent is about the answer. Higher confidence means the information is more likely to be accurate.

Suggested Actions:
After responses, you may see suggested actions:
- Quick buttons for common follow-ups
- Links to related information
- Options to create tickets manually
- Ways to provide feedback

Providing Feedback:
You can rate responses as helpful or not helpful. This feedback helps improve the agent over time.`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'viewing-tickets-from-chat',
    title: 'Viewing Your Tickets from Chat',
    description: 'Learn how to access and view tickets created from chat conversations.',
    category: 'Chat Interface',
    tags: ['tickets', 'chat', 'viewing'],
    readTime: 4,
    demo: <AnimatedChatDemo />,
    content: createArticleContent(
      `When tickets are created from chat, you can easily view and track them without leaving the chat interface.

Accessing Your Tickets:
1. Click "My Tickets" in the chat sidebar
2. You'll see a list of all your tickets
3. Click any ticket to view details

Ticket Information:
Each ticket shows:
- Ticket ID and summary
- Status (open, in_progress, resolved, closed)
- Priority level
- Assigned team
- Created date
- Estimated resolution time

From the ticket view, you can:
- See full conversation history
- Add comments or updates
- Check approval status
- View team responses

Ticket Updates:
You'll receive notifications when:
- Tickets are assigned to a team
- Team members add comments
- Status changes
- Tickets are resolved

Staying in the Loop:
The chat interface keeps you informed about ticket progress. You can continue chatting with your AI agent while tickets are being processed in the background.`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'understanding-ticket-statuses',
    title: 'Understanding Ticket Statuses',
    description: 'Learn what each ticket status means and how tickets move through workflows.',
    category: 'Tickets App',
    tags: ['tickets', 'status', 'workflow'],
    readTime: 5,
    demo: <AnimatedTicketDetailDemo />,
    content: createArticleContent(
      `Tickets move through different statuses as they're processed. Understanding these statuses helps you track progress.

Ticket Statuses:
- Open: Ticket has been created and is waiting for assignment
- In Progress: Ticket is being worked on by the assigned team
- Resolved: Work is complete, waiting for confirmation
- Closed: Ticket is fully resolved and closed

Status Transitions:
1. Open → In Progress: When team starts working
2. In Progress → Resolved: When work is complete
3. Resolved → Closed: After confirmation
4. Any status → Closed: Can be closed at any time if appropriate

Pending Approval:
If a ticket requires approval, it may show "Pending Approval" status until the approver makes a decision.

Best Practices:
- Update status as work progresses
- Mark resolved when work is complete
- Close tickets only after confirmation
- Use status to track team workload
- Keep statuses accurate for reporting`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'using-kanban-board',
    title: 'Using Kanban Board View',
    description: 'Learn how to use the Kanban board to visualize and manage tickets.',
    category: 'Tickets App',
    tags: ['kanban', 'tickets', 'visualization'],
    readTime: 6,
    demo: <DemoTicketBoard />,
    content: createArticleContent(
      `The Kanban board provides a visual way to manage tickets. See all tickets organized by status in columns.

Kanban Columns:
- Open: New tickets waiting for attention
- In Progress: Tickets currently being worked on
- Resolved: Completed tickets awaiting confirmation
- Closed: Fully resolved tickets

Using the Board:
- Drag and drop tickets between columns to update status
- Click tickets to view details
- Filter by team, priority, or assignee
- Search for specific tickets

Benefits:
- Visual overview of all tickets
- Easy status updates via drag and drop
- Quick identification of bottlenecks
- Better team coordination

Tips:
- Keep tickets in the correct column
- Update status as work progresses
- Use filters to focus on specific tickets
- Regularly review the board for workflow optimization`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'creating-first-team',
    title: 'Creating Your First Team',
    description: 'Step-by-step guide to creating your first team in CoreSupport.',
    category: 'Teams & Roles',
    tags: ['teams', 'setup', 'getting-started'],
    readTime: 5,
    content: createArticleContent(
      `Creating your first team is a key step in setting up CoreSupport. Teams help organize your support structure.

Step 1: Plan Your Team
Before creating, decide:
- What will this team handle?
- Who should be on the team?
- Who will manage the team?
- What name best describes the team?

Step 2: Create the Team
1. Go to Admin Console → Teams
2. Click "Create Team"
3. Enter team name (e.g., "IT Support")
4. Add description explaining the team's purpose
5. Save the team

Step 3: Add Team Members
1. Open the team you just created
2. Click "Add Members"
3. Select users from your organization
4. Save changes

Step 4: Assign a Manager (Optional)
1. Select a team member
2. Click "Set as Manager"
3. The manager can now manage team members and view analytics

Step 5: Use the Team
- Assign tickets to the team
- Use in Smart Policies for auto-assignment
- View team analytics and performance

Your first team is ready! Create additional teams as needed to match your organization's structure.`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'teams-roles-together',
    title: 'How Teams and Roles Work Together',
    description: 'Understand how teams and roles combine to create your access control structure.',
    category: 'Teams & Roles',
    tags: ['teams', 'roles', 'access-control'],
    readTime: 7,
    content: createArticleContent(
      `Teams and roles work together to create a comprehensive access control system in CoreSupport.

Teams:
- Organize users into groups
- Route tickets to the right people
- Enable team-based ticket assignment
- Provide structure for your organization

Roles:
- Control what users can do
- Define permissions and access
- Apply across all teams
- Set base capabilities

How They Work Together:
1. User has a Role: Determines what they can do (permissions)
2. User belongs to Teams: Determines which tickets they see
3. Combined Effect: User sees tickets for their teams and can perform actions allowed by their role

Example:
- User: John Doe
- Role: Manager (can manage teams, view analytics)
- Teams: IT Support, Customer Support
- Result: John can manage members of IT and Customer Support teams, view analytics for both, and see tickets assigned to both teams

Best Practices:
- Assign roles based on responsibilities
- Add users to teams based on their work
- Use role hierarchy appropriately
- Review team and role assignments regularly
- Keep structure aligned with your organization`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'getting-most-from-ai',
    title: 'Getting the Most from Your AI Agent',
    description: 'Tips and strategies for maximizing the value of your AI agent.',
    category: 'Best Practices',
    tags: ['ai-agent', 'best-practices', 'optimization'],
    readTime: 8,
    content: createArticleContent(
      `Your AI agent becomes more valuable as you optimize your setup. Follow these strategies to get the most from it.

Knowledge Base Optimization:
- Upload comprehensive documentation
- Keep documents up to date
- Include FAQs and common questions
- Organize documents logically
- Remove outdated information

Smart Policies:
- Create rules for common requests
- Use specific keywords
- Test rules thoroughly
- Review and update regularly
- Align rules with your workflows

Team Structure:
- Organize teams effectively
- Assign tickets appropriately
- Use team hierarchy for complex orgs
- Ensure teams have right skills
- Balance workload across teams

User Training:
- Teach users how to chat effectively
- Explain when tickets are created
- Show how to view tickets
- Provide examples of good questions
- Encourage feedback

Monitoring and Improvement:
- Review ticket creation patterns
- Analyze chat conversations
- Identify common questions
- Update knowledge base based on gaps
- Refine Smart Policies

Continuous Improvement:
- Regularly review and optimize
- Gather user feedback
- Monitor AI agent performance
- Adjust based on metrics
- Keep evolving your setup`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'understanding-keywords-triggers',
    title: 'Understanding Keywords and Triggers',
    description: 'Learn how to create effective keyword lists that accurately trigger your Smart Policies.',
    category: 'Smart Policies',
    tags: ['keywords', 'triggers', 'smart-policies'],
    readTime: 8,
    content: createArticleContent(
      `Keywords are the foundation of Smart Policies. They determine when your AI agent automatically creates tickets. Understanding how to create effective keyword lists is crucial for accurate automation.

How Keywords Work:
- Your AI agent analyzes user conversations for keyword matches
- If ANY keyword from a policy matches, the policy is triggered
- Keywords are case-insensitive
- Partial word matches work (e.g., "install" matches "installation")

Best Practices for Keywords:
1. Be Specific: Use specific terms that clearly indicate the request type
   - Good: "adobe photoshop", "creative suite", "badge access"
   - Avoid: "help", "issue", "problem" (too generic)

2. Include Variations: Add synonyms and common variations
   - "install", "installation", "setup", "configure"
   - "badge", "keycard", "access card", "ID card"

3. Consider Common Phrases: Include how users actually phrase requests
   - "I need", "I want", "can you", "please install"

4. Test Your Keywords: Try different phrasings to ensure coverage
   - "install software" vs "need software installed"
   - "get a badge" vs "badge request"

5. Avoid Overlap: Make sure keywords don't trigger multiple policies
   - If two policies share keywords, the first match wins

6. Regular Updates: Review and update keywords based on actual usage
   - Monitor which tickets are created
   - Add keywords that users actually use
   - Remove keywords that cause false positives

Example Keyword Lists:
Software Installation:
- install, installation, setup, configure, software, adobe, photoshop, creative suite, microsoft office, application

Badge Access:
- badge, keycard, access card, ID card, building access, entry, door access

Hardware Issues:
- laptop, computer, monitor, printer, broken, not working, malfunction, hardware, device

Testing Keywords:
After creating a policy, test it by having someone chat with your AI agent using different phrasings. Make sure:
- Keywords trigger correctly
- No false positives (wrong tickets created)
- All relevant variations are covered
- Users get the right team assignment`
    ),
    demo: <DemoSmartPolicyForm />,
    lastUpdated: '2025-01-15',
  },
  {
    id: 'configuring-priority-categories',
    title: 'Configuring Priority and Categories',
    description: 'Learn how to set appropriate priorities and categories for tickets created by Smart Policies.',
    category: 'Smart Policies',
    tags: ['priority', 'categories', 'configuration'],
    readTime: 6,
    content: createArticleContent(
      `Setting the right priority and category for tickets ensures they're handled appropriately and routed correctly.

Priority Levels:
- Low: Non-urgent requests that can wait
- Medium: Standard requests (default for most policies)
- High: Urgent issues requiring quick attention
- Urgent: Critical issues that need immediate resolution

Choosing Priority:
Consider these factors:
- Business Impact: How does this affect operations?
- User Impact: How many users are affected?
- Timeline: When does this need to be resolved?
- Resources: What resources are required?

Default Priority Guidelines:
- Software Installation: Medium (usually not urgent)
- Hardware Issues: High (can block work)
- Badge Access: Medium (important but not urgent)
- Password Reset: Low (quick fix available)
- System Down: Urgent (affects everyone)

Categories:
Categories help organize and track tickets. Common categories include:
- Software Installation
- Hardware Issues
- Account Management
- Access Permissions
- Network Issues
- Email Problems
- Password Reset
- System Maintenance
- Training Request

Best Practices:
- Use consistent categories across policies
- Align categories with your team structure
- Choose categories that match your reporting needs
- Keep category list manageable (10-15 categories)
- Review categories periodically for relevance`
    ),
    demo: <DemoSmartPolicyForm />,
    lastUpdated: '2025-01-15',
  },
  {
    id: 'managing-multiple-policies',
    title: 'Managing Multiple Smart Policies',
    description: 'Learn how to organize and manage multiple Smart Policies effectively.',
    category: 'Smart Policies',
    tags: ['management', 'organization', 'multiple-policies'],
    readTime: 7,
    content: createArticleContent(
      `As your organization grows, you'll create multiple Smart Policies. Managing them effectively ensures they work together seamlessly.

Organizing Policies:
1. Group by Category: Organize policies by the type of request they handle
2. Name Clearly: Use descriptive names that explain the policy's purpose
3. Document Purpose: Add descriptions explaining when each policy applies
4. Review Regularly: Periodically review all policies for relevance

Policy Priority Order:
When multiple policies could match, the first matching policy wins. Order matters:
- Place more specific policies first
- Generic policies should come last
- Test policy order to ensure correct matching

Deactivating Policies:
- Temporarily disable policies without deleting them
- Useful for seasonal changes or temporary processes
- Keep inactive policies for reference or future use

Updating Policies:
- Update keywords based on user feedback
- Adjust priorities based on actual ticket patterns
- Modify team assignments as your organization changes
- Update estimated resolution times based on real data

Best Practices:
- Keep policies active only when needed
- Document policy decisions and changes
- Test policy changes before activating
- Monitor policy performance and adjust as needed
- Coordinate with team leads when changing assignments`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'testing-activating-policies',
    title: 'Testing and Activating Smart Policies',
    description: 'Step-by-step guide to testing your policies before activating them in production.',
    category: 'Smart Policies',
    tags: ['testing', 'activation', 'validation'],
    readTime: 6,
    content: createArticleContent(
      `Testing Smart Policies before activation ensures they work correctly and don't create unwanted tickets.

Testing Process:
1. Create Policy (Inactive): Create the policy but keep it inactive
2. Review Configuration: Double-check all settings
3. Test Keywords: Have someone test chat with your AI agent using different phrasings
4. Verify Routing: Confirm tickets are assigned to the correct team
5. Check Priority: Ensure priority levels are appropriate
6. Test Approval: If approval is required, test the approval workflow
7. Activate: Once satisfied, activate the policy

Testing Checklist:
- Keywords trigger correctly
- Tickets are created with correct details
- Team assignment is correct
- Priority is appropriate
- Category is correct
- Estimated resolution time is set
- Approval workflow works (if applicable)
- No false positives (wrong tickets created)

Common Issues to Watch For:
- Overly broad keywords causing false matches
- Wrong team assignment
- Incorrect priority levels
- Missing approval configuration
- Policy conflicts (multiple policies matching)

Activation:
1. Review all test results
2. Get approval from relevant team leads
3. Activate the policy
4. Monitor for the first few days
5. Adjust as needed based on actual usage

Remember: It's better to test thoroughly than to fix incorrect tickets later.`
    ),
    demo: <DemoSmartPolicyForm />,
    lastUpdated: '2025-01-15',
  },
  {
    id: 'using-suggested-actions',
    title: 'Using Suggested Actions',
    description: 'Learn how to use suggested actions that appear after AI responses.',
    category: 'Chat Interface',
    tags: ['suggested-actions', 'chat', 'interactions'],
    readTime: 4,
    content: createArticleContent(
      `Suggested actions are quick buttons that appear after AI responses. They help you take next steps without typing.

What Are Suggested Actions:
- Quick action buttons based on the AI's response
- Appear automatically after relevant responses
- Provide one-click access to common tasks
- Save time by eliminating typing

Examples of Suggested Actions:
- "Create a ticket" - When you need something tracked
- "View my tickets" - Quick access to your tickets
- "Learn more" - Links to relevant documentation
- "Reset password" - Direct action for password issues
- "Contact support" - Escalate to human agent

Using Suggested Actions:
1. Read the AI's response
2. Look for suggested action buttons below the response
3. Click the action that matches what you need
4. The action is executed automatically

Benefits:
- Faster interaction - no need to type
- Clear next steps - AI suggests what to do
- Consistent actions - same options every time
- Reduces errors - predefined actions are accurate

Tips:
- Use suggested actions when they match your needs
- You can still type your own message if needed
- Suggested actions are optional - use what's helpful
- Actions adapt based on the conversation context`
    ),
    demo: <AnimatedChatDemo />,
    lastUpdated: '2025-01-15',
  },
  {
    id: 'providing-feedback',
    title: 'Providing Feedback',
    description: 'Learn how to provide feedback to help improve your AI agent.',
    category: 'Chat Interface',
    tags: ['feedback', 'ratings', 'improvement'],
    readTime: 3,
    content: createArticleContent(
      `Your feedback helps improve the AI agent. Providing feedback is simple and takes just a moment.

How to Provide Feedback:
After receiving an AI response, you'll see feedback options:
- Thumbs up: Response was helpful
- Thumbs down: Response needs improvement

Why Feedback Matters:
- Helps the AI learn what works
- Improves future responses
- Identifies knowledge gaps
- Enhances the overall experience

When to Provide Feedback:
- Positive feedback: When the response was accurate and helpful
- Negative feedback: When the response was incorrect or unhelpful
- Both help: The AI learns from both positive and negative feedback

What Happens with Feedback:
- Feedback is collected and analyzed
- Used to improve AI responses over time
- Helps identify areas for knowledge base updates
- Contributes to overall system improvement

Your feedback is valuable and helps make the AI agent better for everyone.`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'managing-conversations',
    title: 'Managing Conversations',
    description: 'Learn how to manage multiple conversations and conversation history.',
    category: 'Chat Interface',
    tags: ['conversations', 'history', 'management'],
    readTime: 5,
    content: createArticleContent(
      `You can have multiple conversations with your AI agent. Each conversation is saved and can be accessed later.

Creating New Conversations:
- Click the "+" button or "New Chat" option
- Start a fresh conversation
- Previous conversations remain accessible

Conversation History:
- All conversations are saved automatically
- Access history from the sidebar
- Search through past conversations
- Continue previous conversations

Managing Conversations:
- Rename conversations for easy identification
- Delete conversations you no longer need
- Organize by topic or date
- Archive important conversations

Best Practices:
- Use descriptive names for conversations
- Keep conversations organized by topic
- Delete old conversations periodically
- Use new conversations for new topics

Conversation Features:
- Full history of all messages
- Context preserved within each conversation
- Easy navigation between conversations
- Search functionality for finding past chats`
    ),
    demo: <AnimatedChatDemo />,
    lastUpdated: '2025-01-15',
  },
  {
    id: 'assigning-tickets-team-members',
    title: 'Assigning Tickets to Team Members',
    description: 'Learn how to assign tickets to specific team members for faster resolution.',
    category: 'Tickets App',
    tags: ['assignment', 'team-members', 'tickets'],
    readTime: 5,
    demo: <AnimatedTicketDetailDemo />,
    content: createArticleContent(
      `Assigning tickets to specific team members ensures they're handled by the right person quickly.

When to Assign:
- Ticket requires specific expertise
- Team member is available and capable
- Ticket matches a team member's specialty
- You want to ensure accountability

How to Assign:
1. Open the ticket
2. Find the "Assign To" field
3. Select a team member from the dropdown
4. Save the assignment
5. The team member receives notification

Assignment Options:
- Assign to Team: Ticket goes to the team (any member can pick it up)
- Assign to Individual: Specific person is responsible
- Auto-assignment: Smart Policies can assign automatically

Best Practices:
- Assign based on expertise and availability
- Communicate with team members before assigning
- Consider workload when assigning
- Use team assignment for general tickets
- Use individual assignment for specialized tickets

Notifications:
When a ticket is assigned:
- Team member receives email notification
- Ticket appears in their assigned tickets
- Status updates to "in_progress" (if appropriate)
- Assignment is logged in ticket history`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'filtering-searching-tickets',
    title: 'Filtering and Searching Tickets',
    description: 'Learn how to efficiently find tickets using filters and search.',
    category: 'Tickets App',
    tags: ['filtering', 'search', 'tickets'],
    readTime: 5,
    demo: <AnimatedTicketListDemo />,
    content: createArticleContent(
      `With many tickets, finding the right one quickly is essential. Use filters and search to locate tickets efficiently.

Search Functionality:
- Search by ticket ID, summary, or description
- Search across all tickets
- Real-time search results
- Highlighted matches

Filter Options:
- Status: Open, In Progress, Resolved, Closed
- Priority: Low, Medium, High, Urgent
- Team: Filter by assigned team
- Assignee: Filter by assigned person
- Category: Filter by ticket category
- Date Range: Filter by creation or update date

Using Filters:
1. Click the filter icon or use filter dropdowns
2. Select filter criteria
3. Filters apply immediately
4. Multiple filters can be combined
5. Clear filters to see all tickets again

Quick Filters:
- "My Tickets": See only tickets assigned to you
- "Assigned to Me": Personal assignment filter
- "Unassigned": Tickets not yet assigned
- "High Priority": Urgent tickets only

Search Tips:
- Use ticket ID for exact matches
- Search for keywords in descriptions
- Combine search with filters
- Save common filter combinations

Best Practices:
- Use filters to focus on relevant tickets
- Combine search and filters for precision
- Clear filters when done
- Use saved filter combinations for common views`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'updating-ticket-priority',
    title: 'Updating Ticket Priority',
    description: 'Learn when and how to update ticket priority levels.',
    category: 'Tickets App',
    tags: ['priority', 'tickets', 'updates'],
    readTime: 4,
    demo: <AnimatedTicketDetailDemo />,
    content: createArticleContent(
      `Ticket priority can change as situations evolve. Updating priority ensures tickets are handled appropriately.

When to Update Priority:
- Situation becomes more urgent
- Business impact increases
- Deadline approaches
- More users are affected
- Issue escalates in severity

How to Update:
1. Open the ticket
2. Find the Priority field
3. Select new priority level
4. Optionally add a note explaining the change
5. Save the update

Priority Escalation:
- Low → Medium: Standard escalation
- Medium → High: Increased urgency
- High → Urgent: Critical situation
- Can escalate or de-escalate as needed

Best Practices:
- Update priority when circumstances change
- Add notes explaining priority changes
- Communicate priority changes to stakeholders
- Don't mark everything as urgent
- Use priority levels appropriately

Priority Guidelines:
- Low: Can wait, no immediate impact
- Medium: Standard priority (default)
- High: Urgent, needs attention soon
- Urgent: Critical, immediate attention required`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'requesting-approvals-tickets',
    title: 'Requesting Approvals for Tickets',
    description: 'Learn how to request approval for tickets that require authorization.',
    category: 'Tickets App',
    tags: ['approvals', 'requests', 'workflow'],
    readTime: 6,
    content: createArticleContent(
      `Some tickets require approval before they can be processed. Learn how to request approvals effectively.

When to Request Approval:
- Policy requires approval (e.g., software installation)
- Budget approval needed
- Manager authorization required
- Compliance or security review needed
- Resource allocation requires approval

How to Request Approval:
1. Open the ticket
2. Click "Request Approval" button
3. Select approver from your organization
4. Enter approval description
5. Set priority and due date (optional)
6. Submit approval request

Approval Request Details:
- Title: Clear description of what needs approval
- Description: Detailed explanation of the request
- Approver: Who needs to approve
- Priority: How urgent is the approval
- Due Date: When approval is needed by

What Happens Next:
1. Approver receives email notification
2. Approval request appears in ticket
3. Approver reviews and makes decision
4. Requester receives notification of decision
5. Ticket proceeds based on decision

Best Practices:
- Provide clear context in approval requests
- Choose appropriate approvers
- Set realistic due dates
- Follow up if approval is delayed
- Document approval decisions

Communication:
- Keep approval requests clear and concise
- Include relevant context and background
- Explain why approval is needed
- Provide any supporting information`
    ),
    demo: <DemoApprovalFlow />,
    lastUpdated: '2025-01-15',
  },
  {
    id: 'resolving-closing-tickets',
    title: 'Resolving and Closing Tickets',
    description: 'Learn the proper process for resolving and closing tickets.',
    category: 'Tickets App',
    tags: ['resolution', 'closing', 'workflow'],
    readTime: 5,
    demo: <AnimatedTicketDetailDemo />,
    content: createArticleContent(
      `Properly resolving and closing tickets ensures accurate tracking and reporting.

Resolution Process:
1. Complete the work or resolve the issue
2. Update ticket status to "Resolved"
3. Add resolution notes explaining what was done
4. Wait for confirmation from requester
5. Close ticket after confirmation

Status: Resolved
- Work is complete
- Waiting for requester confirmation
- Ticket can be reopened if needed
- Status indicates completion

Status: Closed
- Fully resolved and confirmed
- Final status for the ticket
- Can be reopened if issue returns
- Ticket is archived

Best Practices:
- Mark resolved only when work is complete
- Add detailed resolution notes
- Wait for requester confirmation before closing
- Keep tickets accurate for reporting
- Document solutions for future reference

Resolution Notes:
- Explain what was done
- Include steps taken
- Note any follow-up needed
- Document solutions for knowledge base
- Help others learn from the resolution

Closing Tickets:
- Close only after confirmation
- Don't close prematurely
- Keep tickets open if follow-up is needed
- Use closed status for completed work
- Maintain accurate ticket lifecycle`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'team-based-ticket-management',
    title: 'Team-Based Ticket Management',
    description: 'Learn how teams work together to manage and resolve tickets.',
    category: 'Tickets App',
    tags: ['teams', 'collaboration', 'management'],
    readTime: 6,
    content: createArticleContent(
      `Team-based ticket management enables collaboration and efficient workload distribution.

How Teams Work:
- Tickets can be assigned to teams
- Team members can see and work on team tickets
- Teams can have specialized expertise
- Workload is distributed across team members

Team Views:
- Team Kanban: See all team tickets in board view
- Team List: View team tickets in list format
- Team Analytics: Track team performance and metrics

Collaboration Features:
- Team members can see all team tickets
- Anyone on the team can pick up tickets
- Comments and updates are visible to the team
- Team members can collaborate on solutions

Best Practices:
- Assign tickets to appropriate teams
- Use team views for better organization
- Collaborate through comments
- Share knowledge within teams
- Track team performance

Team Workflow:
1. Ticket assigned to team
2. Team members see ticket in team view
3. Team member picks up ticket
4. Team collaborates on resolution
5. Ticket resolved and closed

Benefits:
- Better workload distribution
- Specialized expertise access
- Collaborative problem-solving
- Improved response times
- Better team coordination`
    ),
    demo: <DemoTicketBoard />,
    lastUpdated: '2025-01-15',
  },
  {
    id: 'adding-members-teams',
    title: 'Adding Members to Teams',
    description: 'Step-by-step guide to adding users to teams.',
    category: 'Teams & Roles',
    tags: ['teams', 'members', 'management'],
    readTime: 4,
    content: createArticleContent(
      `Adding members to teams is straightforward. Team members can then see and work on tickets assigned to their teams.

How to Add Members:
1. Go to Admin Console → Teams
2. Select the team you want to modify
3. Click "Add Members" or "Manage Members"
4. Select users from your organization
5. Save changes

Member Management:
- Add multiple members at once
- Remove members when they leave the team
- View all team members
- See member roles and permissions

Team Member Roles:
- Regular Member: Can work on team tickets
- Manager: Can manage team members and view analytics
- Lead: Can assign tickets and coordinate work

Best Practices:
- Add members based on expertise
- Keep teams appropriately sized
- Update teams when members change roles
- Remove members who no longer need access
- Assign managers for accountability

Team Structure:
- Teams can have multiple members
- Members can belong to multiple teams
- Team membership determines ticket visibility
- Members see tickets assigned to their teams`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'setting-up-team-managers',
    title: 'Setting Up Team Managers',
    description: 'Learn how to assign team managers and their responsibilities.',
    category: 'Teams & Roles',
    tags: ['managers', 'teams', 'leadership'],
    readTime: 5,
    content: createArticleContent(
      `Team managers oversee team operations and have additional permissions. Setting up managers helps organize your support structure.

Manager Responsibilities:
- Manage team members (add/remove)
- View team analytics and metrics
- Assign tickets to team members
- Coordinate team workload
- Review team performance

How to Set Manager:
1. Go to Admin Console → Teams
2. Select the team
3. Find the team member
4. Click "Set as Manager"
5. Manager permissions are granted

Manager Permissions:
- Add and remove team members
- View team analytics
- Assign tickets within team
- Access team reports
- Manage team settings

Best Practices:
- Choose managers who can handle responsibility
- Ensure managers have time for management tasks
- One manager per team is usually sufficient
- Managers should have relevant expertise
- Communicate manager responsibilities clearly

Team Structure:
- Manager: Oversees team operations
- Members: Work on team tickets
- Clear hierarchy helps organization
- Managers coordinate team activities
- Members focus on ticket resolution`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'understanding-team-hierarchy',
    title: 'Understanding Team Hierarchy',
    description: 'Learn how to create nested teams for complex organizational structures.',
    category: 'Teams & Roles',
    tags: ['hierarchy', 'teams', 'organization'],
    readTime: 6,
    content: createArticleContent(
      `Team hierarchy allows you to create nested teams that reflect your organization's structure.

What is Team Hierarchy:
- Parent teams contain sub-teams
- Sub-teams inherit some properties from parents
- Useful for large organizations
- Matches organizational structure

Creating Hierarchy:
1. Create parent team first (e.g., "IT")
2. Create sub-teams (e.g., "IT Support", "IT Infrastructure")
3. Set sub-team's parent in team settings
4. Organize members appropriately

Example Hierarchy:
- IT (Parent Team)
  - IT Support (Sub-team)
  - IT Infrastructure (Sub-team)
  - IT Security (Sub-team)
- HR (Parent Team)
  - HR Operations (Sub-team)
  - HR Benefits (Sub-team)

Benefits:
- Reflects organizational structure
- Better organization for large teams
- Clearer reporting structure
- Easier management of complex organizations
- Supports growth and scaling

Best Practices:
- Keep hierarchy simple (2-3 levels max)
- Use hierarchy for large organizations
- Align with actual org structure
- Review hierarchy periodically
- Don't over-complicate structure`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'assigning-roles-users',
    title: 'Assigning Roles to Users',
    description: 'Learn how to assign roles to control user access and permissions.',
    category: 'Teams & Roles',
    tags: ['roles', 'users', 'permissions'],
    readTime: 5,
    content: createArticleContent(
      `Roles control what users can do in CoreSupport. Assigning appropriate roles ensures users have the right access.

Default Roles:
- Admin: Full access to all features
- Manager: Can manage teams and view analytics
- User: Basic access to chat and tickets

Assigning Roles:
1. Go to Admin Console → Users
2. Find the user
3. Click edit
4. Select role from dropdown
5. Save changes

Role Permissions:
Each role has specific permissions:
- Admin: All permissions
- Manager: Team management, analytics, ticket management
- User: Chat, ticket creation, own tickets

Custom Roles:
- Create custom roles with specific permissions
- Assign permissions based on needs
- Use for specialized access requirements
- Test custom roles before assigning

Best Practices:
- Assign roles based on responsibilities
- Use principle of least privilege
- Review role assignments regularly
- Update roles when responsibilities change
- Document role permissions

Role Changes:
- Users can have roles changed
- Changes take effect immediately
- Previous access is revoked
- New permissions are granted
- Communicate role changes to users`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'creating-custom-roles',
    title: 'Creating Custom Roles',
    description: 'Step-by-step guide to creating custom roles with specific permissions.',
    category: 'Teams & Roles',
    tags: ['roles', 'custom', 'permissions'],
    readTime: 7,
    content: createArticleContent(
      `Custom roles allow you to create roles tailored to your organization's specific needs.

When to Create Custom Roles:
- Default roles don't fit your needs
- You need specialized permissions
- Different departments need different access
- You want granular permission control

Creating a Custom Role:
1. Go to Admin Console → Roles
2. Click "Create Role"
3. Enter role name and description
4. Select permissions from available list
5. Save the role
6. Assign to users

Available Permissions:
- admin_access: Full administrative access
- manage_users: Create and manage users
- manage_teams: Create and manage teams
- manage_roles: Create and manage roles
- manage_settings: Configure system settings
- read_documents: View knowledge base documents
- write_documents: Upload and edit documents
- manage_teams: Team management
- view_analytics: Access analytics and reports
- manage_tickets: Full ticket management
- create_tickets: Create tickets
- view_tickets: View tickets

Best Practices:
- Use clear, descriptive role names
- Document what each permission does
- Start with minimal permissions, add as needed
- Test roles before assigning
- Review custom roles periodically

Role Testing:
- Create role and assign to test user
- Verify permissions work correctly
- Test all expected functionality
- Adjust permissions as needed
- Document role usage

Remember: Custom roles give you flexibility but require careful management.`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'using-teams-ticket-assignment',
    title: 'Using Teams for Ticket Assignment',
    description: 'Learn how teams are used in Smart Policies for automatic ticket assignment.',
    category: 'Teams & Roles',
    tags: ['teams', 'assignment', 'smart-policies'],
    readTime: 6,
    content: createArticleContent(
      `Teams are central to ticket assignment in CoreSupport. Smart Policies use teams to automatically route tickets to the right people.

How Teams Work in Smart Policies:
- Policies specify which team should receive tickets
- Tickets are automatically assigned to the team
- Team members can see and work on team tickets
- Workload is distributed across the team

Setting Up Teams for Policies:
1. Create teams that match your support structure
2. Add appropriate members to each team
3. Configure Smart Policies to assign to teams
4. Teams receive tickets automatically
5. Team members pick up tickets as available

Team Assignment Benefits:
- Automatic routing via Smart Policies
- Workload distribution across team members
- Specialized teams handle specific requests
- Better organization and tracking
- Improved response times

Round-Robin Assignment:
- If multiple team members available, tickets are distributed evenly
- Ensures fair workload distribution
- Prevents tickets from piling up on one person
- Automatic balancing of assignments

Best Practices:
- Create teams that match your structure
- Ensure teams have appropriate members
- Use team assignment in Smart Policies
- Monitor team workload
- Adjust team assignments as needed

Team Configuration:
- Team name should clearly indicate purpose
- Add members with relevant expertise
- Assign managers for coordination
- Use teams consistently in policies
- Review team effectiveness regularly`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'organizing-teams-effectively',
    title: 'Organizing Your Teams Effectively',
    description: 'Best practices for structuring teams to maximize efficiency.',
    category: 'Best Practices',
    tags: ['teams', 'organization', 'structure'],
    readTime: 7,
    content: createArticleContent(
      `Effective team organization is crucial for efficient ticket management and support operations.

Team Structure Principles:
1. Match Organizational Structure: Teams should reflect your actual organization
2. Clear Responsibilities: Each team should have a clear purpose
3. Appropriate Size: Teams should be large enough to handle workload but not too large
4. Specialized Expertise: Teams should have relevant skills for their domain

Common Team Structures:
- Functional Teams: Organized by function (IT, HR, Support)
- Product Teams: Organized by product or service
- Geographic Teams: Organized by location
- Hybrid: Combination of approaches

Team Size Guidelines:
- Small Teams (2-4 members): Specialized, high expertise
- Medium Teams (5-10 members): Standard support teams
- Large Teams (10+ members): May need sub-teams

Best Practices:
- Start with functional teams (IT, HR, Support)
- Add specialized teams as needed
- Keep teams focused on specific areas
- Ensure teams have clear boundaries
- Review and adjust structure regularly

Team Naming:
- Use clear, descriptive names
- Indicate team purpose
- Keep names consistent
- Avoid abbreviations unless standard
- Make names searchable

Team Management:
- Assign managers for accountability
- Regular team reviews
- Monitor team performance
- Adjust team structure as organization grows
- Document team responsibilities`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'rbac-strategies',
    title: 'Role-Based Access Control Strategies',
    description: 'Strategic approaches to implementing RBAC in your organization.',
    category: 'Best Practices',
    tags: ['rbac', 'access-control', 'security'],
    readTime: 8,
    content: createArticleContent(
      `Role-Based Access Control (RBAC) is essential for security and proper access management. Here are strategies for effective implementation.

RBAC Principles:
1. Principle of Least Privilege: Grant minimum necessary permissions
2. Separation of Duties: Different roles for different functions
3. Regular Review: Periodically review role assignments
4. Clear Documentation: Document what each role can do

Role Design Strategies:
- Start with Default Roles: Use Admin, Manager, User as base
- Create Custom Roles: Add specialized roles as needed
- Permission-Based: Assign specific permissions, not broad access
- Department-Based: Consider department-specific roles
- Function-Based: Roles based on job functions

Implementation Steps:
1. Identify access needs across organization
2. Map needs to roles and permissions
3. Create roles with appropriate permissions
4. Assign roles to users
5. Test and validate access
6. Document role structure
7. Review and update regularly

Common Role Patterns:
- Admin: Full access for system administrators
- Manager: Team and analytics access for managers
- Support Agent: Ticket management for support staff
- User: Basic access for end users
- Specialist: Custom roles for specialized functions

Best Practices:
- Document all roles and permissions
- Review role assignments quarterly
- Remove access when roles change
- Use role hierarchy appropriately
- Test role changes before applying

Security Considerations:
- Limit admin access
- Use roles to enforce policies
- Audit role assignments regularly
- Remove access promptly when needed
- Monitor role usage and access patterns`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'document-organization-tips',
    title: 'Document Organization Tips',
    description: 'Best practices for organizing your knowledge base documents.',
    category: 'Best Practices',
    tags: ['documents', 'knowledge-base', 'organization'],
    readTime: 6,
    content: createArticleContent(
      `Well-organized documents help your AI agent provide accurate responses. Follow these tips for effective organization.

Document Organization Principles:
1. Logical Structure: Organize by topic or department
2. Clear Naming: Use descriptive, searchable file names
3. Comprehensive Coverage: Include all relevant information
4. Regular Updates: Keep documents current
5. Version Control: Remove outdated documents

Document Categories:
- Policies: Company policies and procedures
- Procedures: Step-by-step guides
- FAQs: Common questions and answers
- Product Documentation: Product-specific information
- Training Materials: Onboarding and training content

Naming Conventions:
- Use clear, descriptive names
- Include dates for versioned documents
- Use consistent naming patterns
- Make names searchable
- Avoid abbreviations unless standard

Best Practices:
- Upload comprehensive documentation
- Organize by department or topic
- Keep documents up to date
- Remove outdated information
- Review documents periodically

Document Quality:
- Clear, well-written content
- Accurate information
- Comprehensive coverage
- Easy to understand
- Properly formatted

Your AI agent learns from these documents, so quality and organization directly impact response accuracy.`
    ),
    lastUpdated: '2025-01-15',
  },
  {
    id: 'ticket-management-workflows',
    title: 'Ticket Management Workflows',
    description: 'Best practices for efficient ticket management workflows.',
    category: 'Best Practices',
    tags: ['workflows', 'tickets', 'process'],
    readTime: 7,
    content: createArticleContent(
      `Effective ticket management workflows ensure tickets are handled efficiently and nothing falls through the cracks.

Standard Workflow:
1. Ticket Created (automatically or manually)
2. Ticket Assigned (to team or individual)
3. Ticket In Progress (work begins)
4. Ticket Resolved (work complete)
5. Ticket Closed (confirmed and archived)

Workflow Best Practices:
- Use consistent status updates
- Update status as work progresses
- Add comments for important updates
- Communicate with requesters
- Document resolutions

Automation:
- Use Smart Policies for automatic creation
- Auto-assign to teams based on policies
- Set up workflows for common requests
- Automate routine tasks
- Reduce manual work

Communication:
- Keep requesters informed
- Provide status updates
- Explain delays if needed
- Confirm resolution
- Request feedback

Tracking:
- Use ticket status for tracking
- Monitor ticket metrics
- Identify bottlenecks
- Measure response times
- Track resolution rates

Continuous Improvement:
- Review workflows regularly
- Identify inefficiencies
- Optimize processes
- Train team on best practices
- Refine workflows over time`
    ),
    demo: <DemoTicketBoard />,
    lastUpdated: '2025-01-15',
  },
  {
    id: 'approval-workflow-best-practices',
    title: 'Approval Workflow Best Practices',
    description: 'Best practices for implementing effective approval workflows.',
    category: 'Best Practices',
    tags: ['approvals', 'workflows', 'best-practices'],
    readTime: 7,
    content: createArticleContent(
      `Effective approval workflows ensure requests are reviewed appropriately without creating bottlenecks.

When to Require Approval:
- Policy compliance requirements
- Budget or cost considerations
- Security or compliance reviews
- Manager authorization needed
- Resource allocation decisions

Approval Design:
- Keep approval requirements minimal
- Choose approvers who can respond quickly
- Set clear approval criteria
- Provide context in requests
- Set realistic timelines

Approval Best Practices:
- Only require approval when necessary
- Choose appropriate approvers
- Provide clear request context
- Set expectations for approval time
- Follow up on pending approvals

Approval Communication:
- Clear approval requests
- Adequate context provided
- Regular follow-up if delayed
- Clear decision communication
- Document approval decisions

Approval Workflow:
1. Request submitted with approval
2. Approver notified immediately
3. Approver reviews request
4. Decision made (approve/reject)
5. Requester notified
6. Ticket proceeds based on decision

Optimization:
- Review approval requirements regularly
- Remove unnecessary approvals
- Streamline approval processes
- Use Smart Policies for automatic routing
- Monitor approval times

Remember: Approvals should enable compliance, not create bottlenecks.`
    ),
    demo: <DemoApprovalFlow />,
    lastUpdated: '2025-01-15',
  },
  {
    id: 'security-best-practices',
    title: 'Security Best Practices',
    description: 'Security best practices for using CoreSupport safely.',
    category: 'Best Practices',
    tags: ['security', 'best-practices', 'safety'],
    readTime: 6,
    content: createArticleContent(
      `Security is important for protecting your organization's data and systems. Follow these best practices.

Access Control:
- Use RBAC to control access
- Grant minimum necessary permissions
- Review access regularly
- Remove access promptly when needed
- Use strong authentication

User Management:
- Verify user identities
- Use secure password policies
- Enable password reset functionality
- Monitor user access
- Remove inactive users

Data Protection:
- Protect sensitive information
- Use encryption for data at rest and in transit
- Regular backups
- Secure document storage
- Limit access to sensitive data

Best Practices:
- Regular security reviews
- Update access as roles change
- Monitor for suspicious activity
- Train users on security
- Document security procedures

Compliance:
- Follow industry regulations
- Maintain audit trails
- Document security measures
- Regular compliance reviews
- Update policies as needed

Your organization's security is important. Follow these practices to maintain a secure environment.`
    ),
    lastUpdated: '2025-01-15',
  },
];

